import { format } from "date-fns";

import { useMemo } from "react";

import { Mono, type ResourceConfig, ResourcePage } from "@/components/resource-page";
import { birthDateMax, birthDateMin, today } from "@/utils/dates";
import type { Translate } from "@/constants/i18n";
import { useTranslation } from "@/store/locale";

interface NonMember {
  id: number;
  visit_date: string;
  first_name: string;
  middle_name: string | null;
  last_name: string | null;
  birth_date: string;
  promo_code_id: number;
  promo_code: string | null;
  club_id: number | null;
  club_name: string | null;
  created_by_id: number | null;
  created_at: string;
}

const day = (value: string) => format(new Date(value), "MM/dd/yyyy");
const fullName = (row: NonMember) =>
  [row.first_name, row.middle_name, row.last_name].filter(Boolean).join(" ");

const build = (t: Translate): ResourceConfig<NonMember> => ({
  title: t("nav.nonMembers"),
  singular: t("nm.singular"),
  endpoint: "/non-members",
  model: "non_member",
  // Newest first, off the primary key — the same default the other lists take.
  defaultSort: { key: "id", order: "desc" },
  columns: [
    {
      key: "id",
      label: t("rp.id"),
      sortKey: "id",
      render: (r) => <span className="text-accent underline underline-offset-4">{r.id}</span>,
    },
    // The visit date is the day it was recorded — the form fixes it to today
    // and locks it — so "Created At" was the same day printed twice.
    {
      key: "visit_date",
      label: t("nm.date"),
      sortKey: "visit_date",
      render: (r) => day(r.visit_date),
    },
    {
      key: "name",
      label: t("nm.name"),
      sortKey: "first_name",
      render: (r) => <span className="text-fg font-medium">{fullName(r)}</span>,
    },
    {
      key: "birth_date",
      label: t("nm.birthDate"),
      sortKey: "birth_date",
      render: (r) => <span className="text-fg-muted">{day(r.birth_date)}</span>,
    },
    {
      key: "promo_code",
      label: t("nm.promoCode"),
      render: (r) => (
        <Mono className="bg-surface-inset text-fg rounded-md px-1.5 py-0.5">
          {r.promo_code ?? "—"}
        </Mono>
      ),
      csv: (r) => r.promo_code,
    },
    { key: "club", label: t("nm.club"), render: (r) => r.club_name ?? "—" },
    // "Actor": who was at the door and wrote it down, which is the only person
    // a visit ever involves — nothing edits one afterwards.
    {
      key: "created_by",
      label: t("nm.actor"),
      render: (r, lookup) => lookup("created_by_id", r.created_by_id),
    },
  ],
  filters: [
    {
      name: "promo_code_id",
      label: t("nm.promoCodeFilter"),
      type: "fk",
      endpoint: "/promo-codes/compact",
    },
    { name: "club_id", label: t("nm.club"), type: "fk", endpoint: "/clubs/compact" },
    { name: "visited_from", label: t("nm.visitedFrom"), type: "date" },
    { name: "visited_to", label: t("nm.visitedTo"), type: "date" },
    { name: "created_by_id", label: t("nm.actor"), type: "fk", endpoint: "/users/compact" },
  ],
  fields: [
    // Both are filled in by `newDefaults` and shown, not chosen: a visit is
    // recorded on the day, at the club the person recording it works from.
    {
      name: "visit_date",
      label: t("nm.date"),
      type: "date",
      required: true,
      disabled: true,
      max: today(),
    },
    { name: "first_name", label: t("nm.firstName"), required: true },
    { name: "middle_name", label: t("nm.middleName") },
    { name: "last_name", label: t("nm.lastName") },
    // The picker will not offer a day the API would refuse.
    {
      name: "birth_date",
      label: t("nm.birthDate"),
      type: "date",
      required: true,
      min: birthDateMin(),
      max: birthDateMax(),
    },
    {
      name: "promo_code_id",
      label: t("nm.promoCode"),
      type: "fk",
      required: true,
      endpoint: "/promo-codes/compact",
    },
    {
      name: "club_id",
      label: t("nm.club"),
      type: "fk",
      endpoint: "/clubs/compact",
      disabled: true,
    },
  ],
  detailLabels: {
    visit_date: t("nm.date"),
    first_name: t("nm.firstName"),
    middle_name: t("nm.middleName"),
    last_name: t("nm.lastName"),
    birth_date: t("nm.birthDate"),
    promo_code: t("nm.promoCode"),
    club_name: t("nm.club"),
  },
  // A visit is a record of a moment: it is written once and never changed, and
  // never deleted either — there is no route behind it.
  noEdit: true,
  noDelete: true,
  // A visit is recorded on the day, at the club the person recording it works from.
  newDefaults: (profile) => ({
    visit_date: format(new Date(), "yyyy-MM-dd"),
    club_id: profile.club?.id.toString() ?? "",
  }),
  toForm: (r) => ({
    visit_date: r.visit_date,
    first_name: r.first_name,
    middle_name: r.middle_name ?? "",
    last_name: r.last_name ?? "",
    birth_date: r.birth_date,
    promo_code_id: String(r.promo_code_id),
    club_id: r.club_id?.toString() ?? "",
  }),
});

function NonMembersPage() {
  const t = useTranslation();
  const config = useMemo(() => build(t), [t]);
  return <ResourcePage config={config} />;
}

export default NonMembersPage;
